import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { Assert } from "abweb/utils/Assert";
import { EnumUtils } from "abweb/utils/EnumUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { ManualPromise } from "abweb/utils/ManualPromise";
var HistoryInitiatedType = /*#__PURE__*/function (HistoryInitiatedType) {
  HistoryInitiatedType[HistoryInitiatedType["BACKWARD"] = 0] = "BACKWARD";
  HistoryInitiatedType[HistoryInitiatedType["FORWARD"] = 1] = "FORWARD";
  HistoryInitiatedType[HistoryInitiatedType["MANUAL"] = 2] = "MANUAL";
  return HistoryInitiatedType;
}(HistoryInitiatedType || {});
const LOG = LogUtils.getLogger(__filename);
export class BrowserRouteChangeContext {
  /**
   * @param popstateRevertFnOverride if provided, when invoked it MUST generate a popstate event or else will have a thread leak!
   */
  constructor(abSDKRouter, currentRouteState, newRouteState, popstateRevertFnOverride) {
    _defineProperty(this, "historyInitiatedType", void 0);
    _defineProperty(this, "retryFn", void 0);
    _defineProperty(this, "syncRevertFn", void 0);
    _defineProperty(this, "popstateListeningCount", 0);
    this.abSDKRouter = abSDKRouter;
    this.currentRouteState = currentRouteState;
    this.newRouteState = newRouteState;
    this.historyInitiatedType = this.calcHistoryInitiatedType();
    const [retryFn, revertFn] = this.calcHistoryFns(this.historyInitiatedType);
    this.retryFn = retryFn;
    this.syncRevertFn = popstateRevertFnOverride ?? revertFn;
  }

  /**
   * Revert the url to the appropriate state (mostly using history forward()/back()) when route change is cancelled.
   * Returns after the url has been updated.
   */
  async revertFn() {
    if (this.popstateListeningCount >= 1) {
      LOG.error(`Currently have ${this.popstateListeningCount} listeners where expect 0.`);
      if (this.popstateListeningCount > 5) {
        // Getting out of hand with mem/thread leak , throw in hope that they can submit a PFS
        throw new Error(`Currently have ${this.popstateListeningCount} listeners, expect 0.`);
      }
    }

    // window.history.{forward|back} functions spawn async tasks, but have void return type.
    // The doc says to listen for corresponding popState event to detect when forward|back have been completed.
    const revertP = new ManualPromise();
    const popstateListener = () => revertP.resolve();
    try {
      this.popstateListeningCount++;
      window.addEventListener("popstate", popstateListener);
      this.syncRevertFn(); // syncRevertFn MUST generate a popstate event!
      await revertP; // resolved once async revertFn causes a popstate event.  If no popstate event, waits forever...
      LOG.debug("revertFn completed as popstate event happened.");
    } finally {
      window.removeEventListener("popstate", popstateListener);
      this.popstateListeningCount--;
    }
  }
  calcHistoryInitiatedType() {
    let retVal;
    // timestamp can be zero while running tests, so do not use falsey check!
    if (this.newRouteState?.__routeState?.timestamp === undefined) {
      // Defensive: no route state available for pop history event,
      // so this is a manual browser url change (bookmark, manual edit)
      retVal = HistoryInitiatedType.MANUAL;
    }
    // Starting state for router.start
    else if (this.currentRouteState === undefined) {
      LOG.debug(`calcHistoryInitiatedType currentRouteState is undefined`);
      retVal = HistoryInitiatedType.FORWARD;
    } else {
      // Back button pressed iff newRouteTS < currentRouteTS
      // const currTS = this.currentRouteState.__routeState.timestamp;
      const currTS = this.currentRouteState.__routeState.timestamp;
      const newTS = this.newRouteState.__routeState.timestamp;
      retVal = newTS < currTS ? HistoryInitiatedType.BACKWARD : HistoryInitiatedType.FORWARD;
      LOG.debug(`calcHistoryInitiatedType currTS=${currTS}, newTS=${newTS}`);
    }
    LOG.info(`calcHistoryInitiatedType returned: ${EnumUtils.enumKeyByEnumValue(HistoryInitiatedType, retVal)}`);
    return retVal;
  }

  /** @returns tuple of retry function (if any), and how to revert the history stack if route change vetoed. */
  calcHistoryFns(historyInitiatedType) {
    switch (historyInitiatedType) {
      case HistoryInitiatedType.BACKWARD:
        return [BrowserRouteChangeContext.RETRY_BACK, this.abSDKRouter.revertForwardFn];
      case HistoryInitiatedType.FORWARD:
        return [BrowserRouteChangeContext.RETRY_FORWARD, this.abSDKRouter.revertBackFn];
      case HistoryInitiatedType.MANUAL:
        return [undefined, this.abSDKRouter.revertBackFn];
      default:
        Assert.assertNever(historyInitiatedType);
    }
  }
}
_defineProperty(BrowserRouteChangeContext, "RETRY_BACK", window.history.back.bind(window.history));
_defineProperty(BrowserRouteChangeContext, "RETRY_FORWARD", window.history.forward.bind(window.history));
//# sourceMappingURL=BrowserRouteChangeContext.js.map
